import React, { useState, useEffect } from "react";
import styles from "./styles/JoinGame.module.css";
import useTranslation from "next-translate/useTranslation";
import { getModeId } from "../../util/util";
import { Invite, useLobby } from "../../ducks/lobby";
import { VARIANT_CHESS960, VARIANT_CHESS } from "@chesshotel/constants";
import { FaUser } from "@react-icons/all-files/fa/FaUser";
import { FaSignal } from "@react-icons/all-files/fa/FaSignal";
import { FaClock } from "@react-icons/all-files/fa/FaClock";
import { FaChessRook } from "@react-icons/all-files/fa/FaChessRook";
import { FaBan } from "@react-icons/all-files/fa/FaBan";
import { FaDiceSix } from "@react-icons/all-files/fa/FaDiceSix";
import { FaBolt } from "@react-icons/all-files/fa/FaBolt";
import { FaDragon } from "@react-icons/all-files/fa/FaDragon";
import { BulletIcon } from "../../icons/BulletIcon";
import { useLeagues } from "../../ducks/leagues";
import { useSettings } from "../../ducks/settings";
import { useUser } from "../../ducks/user";
import { useStats } from "../../ducks/stats";
import { websocket } from "../../services/websocket";

export function acceptInvite(invite, leaguePlay) {
  const inv = {
    id: invite.id,
    leaguePlay,
    time: invite.time,
    inc: invite.increment,
    variant: invite.variant,
  };

  websocket.emit("acce", inv);
}

export default function JoinGame() {
  const { t } = useTranslation("common");

  const [isDesktop, setIsDesktop] = useState(null);

  const { leaguePlay, invites, gamesCreated } = useLobby();
  const { leagueScores } = useLeagues();
  const { maxOpponentRating, minOpponentRating, noGuests } = useSettings();
  const { displayName } = useUser();
  const { chess, chess960 } = useStats();
  const elo = chess.elo;
  const elo960 = chess960.elo;

  useEffect(() => {
    setIsDesktop(window.innerWidth > 350);
  }, []);

  function getGameType(time, variant) {
    if (variant === VARIANT_CHESS960) {
      return (
        <td>
          <div className="flex items-center space-x-1">
            <FaDiceSix />
            <div>960</div>
          </div>
        </td>
      );
    } else if (time < 3) {
      return (
        <td>
          <div className="flex items-center space-x-1">
            <BulletIcon />
            <div>Bullet</div>
          </div>
        </td>
      );
    } else if (time < 10) {
      return (
        <td>
          <div className="flex items-center space-x-1">
            <FaBolt />
            <div>Blitz</div>
          </div>
        </td>
      );
    } else {
      return (
        <td>
          <div className="flex items-center space-x-1">
            <FaDragon />
            <div>Rapid</div>
          </div>
        </td>
      );
    }
  }

  function formatTime(time, inc) {
    let timeStr = "";
    if (inc === 0) {
      timeStr = time + "m";
    } else {
      timeStr = time + "m + " + inc + "s";
    }
    return timeStr;
  }

  function renderCreatedGames() {
    return gamesCreated.map((game, index) => {
      const formatedTime = formatTime(game.time, game.inc);
      let gameElo;
      if (leaguePlay) {
        const modeId = getModeId(game.time, game.variant);
        gameElo = leagueScores[modeId].elo;
      } else {
        gameElo = game.variant === VARIANT_CHESS ? elo : elo960;
      }
      return (
        <tr key={index} className={styles.removing}>
          <td>{displayName}</td>
          <td>{gameElo}</td>
          <td>{formatedTime}</td>
          {getGameType(game.time, game.variant)}
        </tr>
      );
    });
  }

  function getRow(invite: Invite) {
    const username = invite.isGuest
      ? invite.username.substring(2)
      : invite.username;
    const time = formatTime(invite.time, invite.inc);

    let isEnabled = true;
    const classes = [];
    if (invite.removing) {
      classes.push(styles["removing"]);
      isEnabled = false;
    }

    let isBlocked = false;
    if (
      invite.elo > maxOpponentRating ||
      invite.elo < minOpponentRating ||
      (invite.isGuest && noGuests)
    ) {
      classes.push(styles["removing"]);
      isEnabled = false;
      isBlocked = true;
    }

    if (invite.blink) {
      classes.push(styles["blink"]);
      isEnabled = false;
    }
    const keyId = `${username}${time}${invite.variant}`;

    return (
      <tr
        key={keyId}
        className={classes.join(" ")}
        onClick={() => {
          if (isEnabled) {
            acceptInvite(invite, leaguePlay);
          }
        }}
      >
        {isBlocked ? (
          <td>
            <div className="flex items-center space-x-1">
              <FaBan />
              <div>{username}</div>
            </div>
          </td>
        ) : (
          <td>{username}</td>
        )}
        <td>{invite.elo}</td>
        <td>{time}</td>
        {getGameType(invite.time, invite.variant)}
      </tr>
    );
  }

  function renderInvites() {
    const rows = [];
    const chess960invites = [];

    for (let i = 0; i < invites.length; i++) {
      const invite = invites[i];
      if (invite.variant === VARIANT_CHESS960) {
        chess960invites.push(invite);
      } else {
        rows.push(getRow(invite));
      }
    }

    if (chess960invites.length > 0) {
      rows.push(
        <tr key={"variant"}>
          <td colSpan={4} className={styles.variantDivider}>
            {t("Chess960 games")}
          </td>
        </tr>
      );
      for (let i = 0; i < chess960invites.length; i++) {
        const chess960invite = chess960invites[i];
        rows.push(getRow(chess960invite));
      }
    }

    return rows;
  }

  return (
    <div className={styles.joinGame}>
      <div className={styles.lobbyTableCont}>
        <table className={styles.lobbyTable}>
          <colgroup>
            <col width="34%" />
            <col width="22%" />
            <col width="22%" />
            <col width="22%" />
          </colgroup>
          <thead>
            <tr>
              <th>
                <div className="flex items-center space-x-1">
                  <div className="text-orange-400">
                    <FaUser />
                  </div>
                  {isDesktop && <div>{t("Player")}</div>}
                </div>
              </th>
              <th>
                <div className="flex items-center space-x-1">
                  <div className="text-orange-400">
                    <FaSignal />
                  </div>
                  {isDesktop && <div>{t("Rating")}</div>}
                </div>
              </th>
              <th>
                <div className="flex items-center space-x-1">
                  <div className="text-orange-400">
                    <FaClock />
                  </div>
                  {isDesktop && <div>{t("Time")}</div>}
                </div>
              </th>
              <th>
                <div className="flex items-center space-x-1">
                  <div className="text-orange-400">
                    <FaChessRook />
                  </div>
                  {isDesktop && <div>{t("Mode")}</div>}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {renderCreatedGames()}
            {renderInvites()}
          </tbody>
        </table>
      </div>
    </div>
  );
}
